import React, { useState } from 'react';
import { timelineApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Clock } from 'lucide-react';

const TimelinePanel = ({ bookId, events, setEvents, characters, locations }) => {
    const [showNew, setShowNew] = useState(false);
    const [newEvent, setNewEvent] = useState({ title: '', description: '', date_in_story: '', location: '' });

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const response = await timelineApi.create({ 
                ...newEvent, 
                book_id: bookId,
                order: events.length 
            });
            setEvents([...events, response.data]);
            setShowNew(false);
            setNewEvent({ title: '', description: '', date_in_story: '', location: '' });
            toast.success('Event created');
        } catch (error) {
            toast.error('Failed to create event');
        }
    };

    return (
        <div className="p-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl font-semibold">Timeline</h2>
                <Dialog open={showNew} onOpenChange={setShowNew}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-event-btn">
                            <Plus className="h-4 w-4 mr-2" /> New Event
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>New Timeline Event</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreate} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label>Event Title</Label>
                                <Input value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Date in Story</Label>
                                <Input value={newEvent.date_in_story} onChange={(e) => setNewEvent({...newEvent, date_in_story: e.target.value})} placeholder="e.g., Day 1, Year 2045" />
                            </div>
                            <div className="space-y-2">
                                <Label>Description</Label>
                                <Textarea value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} />
                            </div>
                            <div className="flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                                <Button type="submit">Create Event</Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="relative">
                <div className="timeline-track" />
                {events.map((event, index) => (
                    <div key={event.id} className="timeline-item" data-testid={`timeline-event-${event.id}`}>
                        <div className="absolute -left-12 mt-1">
                            <div className="timeline-dot" />
                        </div>
                        <div className="card-default p-4">
                            {event.date_in_story && (
                                <div className="label-text text-accent mb-1">{event.date_in_story}</div>
                            )}
                            <h3 className="font-serif font-semibold">{event.title}</h3>
                            {event.description && (
                                <p className="text-sm text-muted-foreground mt-2">{event.description}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            
            {events.length === 0 && (
                <div className="text-center py-12">
                    <Clock className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                    <h3 className="font-serif font-semibold mb-1">No events yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Add events to track your story's timeline
                    </p>
                    <Button variant="outline" onClick={() => setShowNew(true)}>
                        <Plus className="h-4 w-4 mr-2" /> Add First Event
                    </Button>
                </div>
            )}
        </div>
    );
};

export default TimelinePanel;
